
import { PromoBlockSkeleton } from './PromoBlockSkeleton'
import { Button, usePromoBlockVariants } from '../../../Theme'
import { SectionTitle } from '../../../UiBlocks'
import { LABELS } from '../../lib'

import type { HomepagePromoBlock } from '../../types'

interface PromoBlockProps {
  block: HomepagePromoBlock
  /** Loading state - shows skeleton */
  isLoading?: boolean
}

/**
 * PromoBlock - Promotional block with image + content layout
 *
 * Features:
 * - Responsive layout: stacked on mobile, side-by-side on desktop
 * - Configurable image position (left/right)
 * - CVA variants via UiProvider for consistent styling
 * - Loading skeleton state
 */
export function PromoBlock({ block, isLoading = false }: PromoBlockProps) {
  const {
    promoBlockSectionVariants,
    promoBlockContainerVariants,
    promoBlockImageSectionVariants,
    promoBlockImageLinkVariants,
    promoBlockImageVariants,
    promoBlockContentSectionVariants,
    promoBlockContentInnerVariants,
    promoBlockTitleVariants,
    promoBlockDescriptionVariants,
  } = usePromoBlockVariants()

  // Loading state
  if (isLoading) {
    return <PromoBlockSkeleton imagePosition={block.image_position} />
  }

  const imageSection = block.image && (
    <div className={promoBlockImageSectionVariants()}>
      {block.link ? (
        <a href={block.link} title={block.title} className={promoBlockImageLinkVariants()}>
          <img
            src={block.image}
            alt={block.title}
            width={800}
            height={600}
            loading="lazy"
            className={promoBlockImageVariants()}
          />
        </a>
      ) : (
        <img
          src={block.image}
          alt={block.title}
          width={800}
          height={600}
          loading="lazy"
          className={promoBlockImageVariants()}
        />
      )}
    </div>
  )

  const contentSection = (
    <div className={promoBlockContentSectionVariants()}>
      <div className={promoBlockContentInnerVariants()}>
        <SectionTitle as="h3" className={promoBlockTitleVariants()}>
          {block.title}
        </SectionTitle>
        {block.description && (
          <p className={promoBlockDescriptionVariants()}>
            {block.description}
          </p>
        )}
        {block.link && (
          <Button variant="promoBlock" size="promoBlock" asChild>
            <a href={block.link}>{LABELS.VIEW_DETAILS}</a>
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <section className={promoBlockSectionVariants()}>
      <div className={promoBlockContainerVariants({ imagePosition: block.image_position ?? 'left' })}>
        {imageSection}
        {contentSection}
      </div>
    </section>
  )
}
